import useSWR from 'swr';
import styled, { keyframes } from 'styled-components';

import { requestService } from 'shared/utils/services/requestService';
import { useWindowSize } from 'shared/hooks';
import { variableFontRegular } from 'styles/theme';

const fetchSearchSuggestionsFeed = async (slug: string) => {
  const data = await requestService({ url: `/web-api/v1/cms/cms_articles/${slug}`, method: 'GET' });
  return data?.sections?.[0]?.subsections;
};

export const AnimatedSearchSuggestions = () => {
  const { data: suggestedSearches, error } = useSWR(
    'search-suggestions-feed-web',
    fetchSearchSuggestionsFeed,
    { revalidateOnFocus: false },
  );
  const { windowSize } = useWindowSize();
  const windowWidth = windowSize?.width || 400;
  const searchTextWidth = windowWidth - 250;

  return (
    <AnimatedSearchSuggestions.Suggestion>
      <AnimatedSearchSuggestions.ListOfSuggestions>
        {!error &&
          suggestedSearches?.reverse()?.map((item) => (
            <AnimatedSearchSuggestions.ListItem key={item?.title} $textWidth={searchTextWidth}>
              {item?.title}
            </AnimatedSearchSuggestions.ListItem>
          ))}
      </AnimatedSearchSuggestions.ListOfSuggestions>
    </AnimatedSearchSuggestions.Suggestion>
  );
};

const cannedAnimationDesktop = keyframes`
 0% {opacity:1; transform:translate3d(0,-100%,0);}
  10% {transform:translate3d(0,-95%,0);}
  20% {transform:translate3d(0,-85%,0);}
  30% {transform:translate3d(0,-75%,0);}
  40% {transform:translate3d(0,-65%,0);}
  50% {transform:translate3d(0,-55%,0);}
  60% {transform:translate3d(0,-45%,0);}
  70% {transform:translate3d(0,-35%,0);}
  80% {transform:translate3d(0,-25%,0);}
  90% {transform:translate3d(0,-15%,0);}
  99.9% {transform:translate3d(0,-5%,0);}
  100% { transform:translate3d(0,-100%,0);}
`;

const cannedAnimationMobile = keyframes`
 0% {opacity:1; transform:translate3d(0,-100%,0);}
  10% {transform:translate3d(0,-90%,0);}
  20% {transform:translate3d(0,-80%,0);}
  30% {transform:translate3d(0,-70%,0);}
  40% {transform:translate3d(0,-60%,0);}
  50% {transform:translate3d(0,-50%,0);}
  60% {transform:translate3d(0,-40%,0);}
  70% {transform:translate3d(0,-30%,0);}
  80% {transform:translate3d(0,-20%,0);}
  90% {transform:translate3d(0,-10%,0);}
  99.9% {opacity: 1; transform:translate3d(0,0,0);}
  100% { transform:translate3d(0,-100%,0);}
`;

AnimatedSearchSuggestions.Suggestion = styled.div`
  margin-left: 4px;
  max-width: 280px;
  height: 14px;
  overflow: hidden;
  position: relative;
  line-height: 34px;
  &::before {
    top: 0;
    left: 0;
    z-index: 1;
    width: 100%;
    content: '';
    height: 10px;
    position: absolute;
  }
  &::after {
    left: 0;
    bottom: 0;
    z-index: 1;
    width: 100%;
    content: '';
    height: 10px;
    position: absolute;
  }
`;

AnimatedSearchSuggestions.ListOfSuggestions = styled.ul`
  margin: 0;
  padding: 0;
  animation-name: ${cannedAnimationMobile};
  animation-duration: 12s;
  animation-timing-function: ease-in-out;
  animation-iteration-count: infinite;
  animation-direction: normal;
  animation-delay: 1s; /* BUY3-22014: we need this delay for it to animate on first render on Safari */

  @media (min-width: ${({ theme }) => theme.breakpoints.mediumSmall}px) {
    animation-name: ${cannedAnimationDesktop};
  }
`;

AnimatedSearchSuggestions.ListItem = styled.li<{ $textWidth: number }>`
  opacity: 1;
  height: 20px;
  padding: 10px;
  list-style: none;
  ${variableFontRegular({ size: '11.5px', height: '14px' })};
  white-space: nowrap;
  text-overflow: ellipsis;
  overflow: hidden;
  width: 280px; /* max-width of AnimatedSearchSuggestions.Suggestion */

  @media (max-width: ${({ theme }) => theme.breakpoints.mediumSmall}px) {
    margin: 0 10px;
    padding: 0;
    line-height: 18px;
    width: ${({ $textWidth }) => $textWidth && `${$textWidth}px`};
  }
`;
